import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const IVORY = '#FAF8F3';
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

export default function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo   = searchParams.get('redirect');
  const googleError  = searchParams.get('error') === 'google_failed';

  const { login, isAuthenticated, isLoading: authLoading } = useReaderAuth();

  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPwd,     setShowPwd]     = useState(false);
  const [error,       setError]       = useState(googleError ? 'Google sign-in failed. Please try again.' : '');
  const [submitting,  setSubmitting]  = useState(false);

  const handleGoogleSignIn = () => {
    if (redirectTo) sessionStorage.setItem('reader_auth_redirect', redirectTo);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/courses';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      if (redirectTo === 'buy') window.location.href = STRIPE_PAYMENT_LINK;
      else if (redirectTo) navigate(redirectTo);
      else navigate('/courses');
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email, password);
    if (result.success) {
      if (redirectTo === 'buy') window.location.href = STRIPE_PAYMENT_LINK;
      else if (redirectTo) navigate(redirectTo);
      else navigate('/courses');
    } else {
      setError(result.error);
    }
    setSubmitting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: IVORY }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY }}>
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>

      <div className="pt-20 pb-10 px-6" style={{ backgroundColor: NAVY }}>
        <div className="max-w-md mx-auto">
          <span className="block text-[10px] tracking-[0.3em] uppercase font-sans font-medium mb-4" style={{ color: GOLD + 'B3' }}>
            Reader Portal
          </span>
          <h1 className="font-serif text-4xl font-light tracking-tighter" style={{ color: IVORY }}>
            Welcome back
          </h1>
          <p className="font-sans text-sm font-light mt-2" style={{ color: 'rgba(250,248,243,0.75)' }}>
            Sign in to access your courses and learning resources.
          </p>
        </div>
      </div>

      {/* ── Form card ─────────────────────────────────────────────────────── */}
      <div className="px-6 pb-16">
        <div
          className="max-w-md mx-auto -mt-1 p-8 md:p-10"
          style={{ backgroundColor: 'white', border: '1px solid rgba(7,27,58,0.07)', boxShadow: '0 4px 32px rgba(7,27,58,0.06)' }}
        >
          {/* Google */}
          <button
            onClick={handleGoogleSignIn}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 py-3 px-4 transition-all duration-200 font-sans text-sm font-medium mb-6"
            style={{ border: '1px solid rgba(7,27,58,0.12)', color: NAVY, backgroundColor: 'white' }}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="relative flex items-center mb-6">
            <div className="flex-grow" style={{ borderTop: '1px solid rgba(7,27,58,0.07)' }} />
            <span className="mx-4 font-sans text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(7,27,58,0.3)' }}>or</span>
            <div className="flex-grow" style={{ borderTop: '1px solid rgba(7,27,58,0.07)' }} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-3 p-4" style={{ backgroundColor: 'rgba(192,57,43,0.06)', border: '1px solid rgba(192,57,43,0.15)' }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C0392B' }} />
                <p className="font-sans text-sm" style={{ color: '#C0392B' }}>{error}</p>
              </div>
            )}

            <div>
              <label className="block font-sans text-[11px] uppercase tracking-[0.15em] font-medium mb-2" style={{ color: 'rgba(7,27,58,0.5)' }}>
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(7,27,58,0.3)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full pl-11 pr-4 py-3.5 font-sans text-base outline-none transition-all"
                  style={{ border: '1px solid rgba(7,27,58,0.14)', backgroundColor: 'rgba(7,27,58,0.02)', color: NAVY, fontSize: '17px' }}
                  placeholder="you@example.com"
                  required
                  data-testid="signin-email"
                />
              </div>
            </div>

            <div>
              <label className="block font-sans text-[11px] uppercase tracking-[0.15em] font-medium mb-2" style={{ color: 'rgba(7,27,58,0.5)' }}>
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(7,27,58,0.3)' }} />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full pl-11 pr-12 py-3.5 font-sans text-base outline-none transition-all"
                  style={{ border: '1px solid rgba(7,27,58,0.14)', backgroundColor: 'rgba(7,27,58,0.02)', color: NAVY, fontSize: '17px' }}
                  placeholder="Enter your password"
                  required
                  data-testid="signin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(7,27,58,0.35)' }}
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 font-sans text-sm uppercase tracking-[0.15em] font-medium transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: NAVY, color: IVORY, fontSize: '14px' }}
              data-testid="signin-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in...</> : 'Sign In'}
            </button>
          </form>

          {/* ── New here? two clear paths ───────────────────────────────── */}
          <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(7,27,58,0.07)' }}>
            <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-center mb-4" style={{ color: 'rgba(7,27,58,0.35)' }}>
              New here? Choose where to start
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/signup?redirect=/courses"
                className="flex flex-col items-center p-4 text-center transition-all duration-200 group"
                style={{ border: '1px solid rgba(7,27,58,0.09)', backgroundColor: 'rgba(7,27,58,0.02)' }}
                data-testid="signin-new-courses"
              >
                <span className="font-serif text-base font-light mb-1" style={{ color: NAVY }}>Courses</span>
                <span className="font-sans text-[10px] leading-snug" style={{ color: 'rgba(7,27,58,0.45)' }}>Self-paced cultural fluency modules</span>
                <ArrowRight size={12} className="mt-2 transition-transform duration-200 group-hover:translate-x-1" style={{ color: GOLD }} />
              </Link>
              <Link
                to="/signup?redirect=/books"
                className="flex flex-col items-center p-4 text-center transition-all duration-200 group"
                style={{ border: '1px solid rgba(7,27,58,0.09)', backgroundColor: 'rgba(7,27,58,0.02)' }}
                data-testid="signin-new-ebook"
              >
                <span className="font-serif text-base font-light mb-1" style={{ color: NAVY }}>Ebook Access</span>
                <span className="font-sans text-[10px] leading-snug" style={{ color: 'rgba(7,27,58,0.45)' }}>Cultural Fluency Without Compromise</span>
                <ArrowRight size={12} className="mt-2 transition-transform duration-200 group-hover:translate-x-1" style={{ color: GOLD }} />
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center mt-6 font-sans text-sm" style={{ color: 'rgba(7,27,58,0.4)' }}>
          <Link to="/courses" style={{ color: 'rgba(7,27,58,0.4)' }}>← Back to Courses</Link>
        </p>
      </div>
    </div>
  );
}
